import React from 'react';
import { Building2, Users2, Scale, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { organizationData } from '../data/mock';

const partnerLinks = {
  "DC Association for the Education of Young Children": "dcaeyc",
  "DC Family Child Care Association": "dcfcca",
  "DC Head Start Association": "dcheadstart",
  "Washington Association of Child Care Centers": "waccc",
  "Directors Exchange": "directors-exchange",
  "Multicultural Spanish Speaking Providers Association": "msspa"
};

const OrganizationSection = () => {
  const navigate = useNavigate();

  const handlePartnerClick = (partnerName) => {
    const partnerId = partnerLinks[partnerName];
    if (partnerId) {
      navigate(`/partners/${partnerId}`);
    }
  };

  return (
    <section id="organization" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute top-20 left-20 w-40 h-40 border border-white rounded-full" />
          <div className="absolute top-40 right-40 w-60 h-60 border border-white rounded-full" />
          <div className="absolute bottom-20 left-1/3 w-32 h-32 border border-white rounded-full" />
        </div>
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-semibold mb-4">
            Our Organization
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {organizationData.title}
          </h2>
          <p className="text-slate-300 max-w-3xl mx-auto text-lg leading-relaxed">
            {organizationData.subtitle}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Left - Coalition Info */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">Cross-Sector Coalition</h3>
            </div>
            <p className="text-slate-300 leading-relaxed mb-6">
              {organizationData.description}
            </p>
            <p className="text-slate-400 leading-relaxed">
              {organizationData.legacy}
            </p>
          </div>

          {/* Right - Governance */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
                <Scale className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white">{organizationData.governanceTitle}</h3>
            </div>
            <p className="text-slate-300 leading-relaxed mb-6">
              {organizationData.governanceDescription}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-teal-300">
                <Users2 className="w-5 h-5" />
                <span className="font-semibold">15-Member Board of Directors</span>
              </div>
              <Link 
                to="/board-of-directors"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-300 rounded-full text-sm font-semibold hover:bg-amber-500/30 transition-colors duration-300 group"
                data-testid="meet-board-link"
              >
                Meet Our Board
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>

        {/* Staff Consultants Section */}
        <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-3xl p-8 lg:p-12 border border-violet-500/20 mb-16">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-white mb-3">Our Team</h3>
            <p className="text-slate-400">The dedicated professionals driving our mission forward</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Ihkeem D. Ma'at */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-violet-500/50 transition-all duration-300">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-violet-500/30 flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_19451de5-ade2-4c9c-97e0-a11403f5250c/artifacts/xop1p9jp_Untitled%20design%20%289%29.png"
                    alt="Ihkeem D. Ma'at"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center sm:text-left">
                  <h4 className="text-xl font-bold text-white mb-1">Ihkeem D. Ma'at</h4>
                  <p className="text-violet-300 font-medium mb-3">Collaboration and Advocacy Director</p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Leads cross-sector partnerships and advocacy initiatives to strengthen early childhood education across the District. Oversees operational coordination for DC ELC's leadership, board, and coalition activities.
                  </p>
                </div>
              </div>
            </div>

            {/* Zainab Sulaiman */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-violet-500/50 transition-all duration-300">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-violet-500/30 flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_19451de5-ade2-4c9c-97e0-a11403f5250c/artifacts/r8nv667c_Untitled%20design%20%2810%29.png"
                    alt="Zainab Sulaiman"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center sm:text-left">
                  <h4 className="text-xl font-bold text-white mb-1">Zainab Sulaiman</h4>
                  <p className="text-violet-300 font-medium mb-3">Sr. Programs and Communications Manager</p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Leads organizational narrative development, strategic communications, and program alignment at DC ELC. Guides cohesive messaging, branding, and funder-facing materials to strengthen organizational sustainability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-3xl p-8 lg:p-12 border border-teal-500/20">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-white mb-3">Our Partner Organizations</h3>
            <p className="text-slate-400">A coalition of dedicated associations working together - click to learn more</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizationData.partners.map((partner, index) => (
              <button
                key={index}
                onClick={() => handlePartnerClick(partner)}
                className="flex items-center justify-between gap-3 bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/15 hover:border-teal-500/50 transition-all duration-300 group text-left"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <span className="text-slate-200 text-sm font-medium">{partner}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-teal-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrganizationSection;
